import NiceModal, { useModal } from '@ebay/nice-modal-react'
import {
  Alert,
  AlertTitle,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography
} from '@mui/material'
import { t } from 'i18next'
import { useState } from 'react'
import { useUpdateCompanyUserMutation } from '../../hooks/useMutations/useCompaniesMutations'
import { useCompanyRoles } from '../../hooks/useQueries/useCompanies'
import { useSnackbar } from '../../hooks/useSnackbar'
import { isDefaultRole } from '../../utils/user.utils'
import { LoaderButton } from '../LoaderButton'

export const ChangeRoleModal = NiceModal.create(
  ({
    companyId,
    userId,
    currentRole,
    hasMoreFullAccessUsers,
    workspaceId
  }: {
    companyId: string
    userId: string
    currentRole: string
    hasMoreFullAccessUsers: boolean
    workspaceId?: string
  }) => {
    const modal = useModal()
    const [newRoleId, setNewRoleId] = useState<string | undefined>(undefined)
    const { showSnackbar } = useSnackbar()
    const { data: roles } = useCompanyRoles(companyId)
    const [confirmed, setConfirmed] = useState(false)

    const updateRoleUserMutation = useUpdateCompanyUserMutation(
      companyId,
      workspaceId
    )

    const handleSave = () => {
      updateRoleUserMutation.mutate(
        {
          userId,
          data: { roleId: newRoleId }
        },
        {
          onSuccess: () => {
            showSnackbar({
              message: t('change_role_modal.success'),
              severity: 'success'
            })
          },
          onError: () => {
            showSnackbar({
              message: t('change_role_modal.error'),
              severity: 'error'
            })
          },
          onSettled: () => {
            modal.remove()
          }
        }
      )
    }

    return (
      <Dialog
        open={modal.visible}
        onClose={() => modal.remove()}
        sx={{
          '& .MuiDialog-paper': {
            width: '32.5625rem',
            padding: '1.5rem',
            borderRadius: '.25rem'
          }
        }}
      >
        <DialogTitle sx={{ py: '1.5rem' }}>
          {t('change_role_modal.title')}
        </DialogTitle>
        <DialogContent sx={{ padding: '1.5rem' }}>
          <Stack direction='column' spacing='1.5rem'>
            <Typography variant='body1'>
              {t('change_role_modal.subtitle')}
            </Typography>
            <FormControl fullWidth>
              <InputLabel id='role-select-label'>
                {t('change_role_modal.placeholder')}
              </InputLabel>
              <Select
                labelId='role-select-label'
                label={t('change_role_modal.placeholder')}
                value={newRoleId || ''}
                onChange={e => setNewRoleId(e.target.value)}
                disabled={!hasMoreFullAccessUsers}
              >
                {roles
                  ?.filter(role => role.name !== currentRole)
                  ?.map(role => (
                    <MenuItem key={role.id} value={role.id}>
                      {isDefaultRole(role.name)
                        ? t(`role.${role.name}`)
                        : role.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            {!hasMoreFullAccessUsers && (
              <Alert
                severity='error'
                sx={{
                  mt: '1.5rem',
                  color: '#F4C7C7',
                  backgroundColor: '#160B0B'
                }}
              >
                <AlertTitle>
                  {t('change_role_modal.alerts.full_access.title')}
                </AlertTitle>
                {t('change_role_modal.alerts.full_access.description')}
              </Alert>
            )}
            {workspaceId && hasMoreFullAccessUsers && (
              <>
                <Alert severity='warning'>
                  <AlertTitle>
                    {t('change_role_modal.alerts.workspace.title')}
                  </AlertTitle>
                  {t('change_role_modal.alerts.workspace.description')}
                </Alert>
                <FormControlLabel
                  sx={{ marginTop: '1rem' }}
                  control={
                    <Checkbox
                      checked={confirmed}
                      onChange={e => setConfirmed(e.target.checked)}
                    />
                  }
                  label={t('change_role_modal.confirmation_text')}
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            variant='outlined'
            color='secondary'
            onClick={() => modal.remove()}
            sx={{
              textTransform: 'none',
              width: '14rem',
              padding: '.5rem 1.375rem',
              color: 'secondary.light',
              backgroundColor: 'custom.semiTransparentIndigo'
            }}
          >
            {t('common.cancel')}
          </Button>
          <LoaderButton
            variant='contained'
            sx={{
              width: '14rem',
              textTransform: 'capitalize',
              padding: '.5rem 1.375rem',
              color: 'rgba(0, 0, 0, 0.87)'
            }}
            loading={updateRoleUserMutation.isPending}
            label={
              updateRoleUserMutation.isPending
                ? t('change_role_modal.changing_role')
                : t('change_role_modal.change_role')
            }
            onClick={handleSave}
            disabled={
              !newRoleId ||
              updateRoleUserMutation.isPending ||
              !hasMoreFullAccessUsers ||
              (workspaceId !== undefined && !confirmed)
            }
          />
        </DialogActions>
      </Dialog>
    )
  }
)
